import { z } from 'zod';
import {
  DEFAULT_LEARN_MORE_CONTENT,
  LearnMoreContentDto,
  SectionContentSchema,
} from '../../learn-more.dto';
import {
  AssignmentConfiguration,
  AssignmentDueConfiguration,
  InputType,
  QuestionType,
  StatusKeyEnum,
  ValidationConfig,
} from '../../../../../types';

export interface SurveyDto {
  id: number;
  name: string;
  key: string;
  displayName: string;
  summary: string;
  learnMoreContent: LearnMoreContentDto;
  llmLearnMoreContent: LearnMoreContentDto;
  isActionSurvey: boolean;
  assignmentConfiguration: AssignmentConfiguration;
  initialCheckpointDueAt: AssignmentDueConfiguration;
}

export const CreatePromptJSONSchema = z.object({
  title: z.string().min(1, 'Prompt title is required'),
  text: z.string().min(1, 'Prompt text is required'),
});
export type CreatePromptJSONDto = z.infer<typeof CreatePromptJSONSchema>;

export const CompletionActionConfigSchema = z.object({
  statusChangeRequestKey: z.nativeEnum(StatusKeyEnum).optional(),
});

export const CreateCheckpointJSONSchema = z.object({
  title: z.string().optional(),
  message: z.string().optional(),
  summary: z.string().optional(),
  dueDateOffsetDays: z.number().optional(),
  isCompletionCheckpoint: z.boolean().optional(),
  reminderBaseDateQuestionKey: z.string().optional(),
  completionActionConfig: CompletionActionConfigSchema.optional().nullable(),
});
export type CreateCheckpointJSONDto = z.infer<
  typeof CreateCheckpointJSONSchema
>;

export const CreateOptionJSONSchema = z.object({
  sortOrder: z.number(),
  text: z.string(),
  isVerificationOption: z.boolean().optional(),
  isRefusalOption: z.boolean().optional(),
  isAlreadyCompletedOption: z.boolean().optional(),
  isCompletedWithReemaOption: z.boolean().optional(),
  nextQuestionKey: z.string().optional(),
  checkpoint: CreateCheckpointJSONSchema.optional(),
  prompt: CreatePromptJSONSchema.optional(),
});
export type CreateOptionJSONDto = z.infer<typeof CreateOptionJSONSchema>;

export const ValidationConfigSchema = z.object({
  isRequired: z.boolean().optional(),
  minDateOffset: z.number().optional(),
  maxDateOffset: z.number().optional(),
});

export const CreateQuestionJSONSchema = z.object({
  sortOrder: z.number(),
  title: z.string().optional(),
  text: z.string().optional(),
  type: z.nativeEnum(InputType).optional(),
  isRequired: z.boolean().optional(),
  isActionTakenDateQuestion: z.boolean().optional(),
  isExpectedCompletionDateQuestion: z.boolean().optional(),
  key: z.string().optional(),
  nextQuestionKey: z.string().optional(),
  options: z.array(CreateOptionJSONSchema).optional(),
  checkpoint: CreateCheckpointJSONSchema.optional(),
  prompt: CreatePromptJSONSchema.optional(),
  validationConfiguration: ValidationConfigSchema.optional(),
  searchKey: z.string().optional(),
});
export type CreateQuestionJSONDto = z.infer<typeof CreateQuestionJSONSchema>;

export const CreateSurveyVersionJSONSchema = z.object({
  defaultFinalCheckpointTitle: z.string().optional(),
  defaultFinalCheckpointMessage: z.string().optional(),
  defaultFinalCheckpointSummary: z.string().optional(),
  initialQuestionKey: z.string().optional(),
  questions: z.array(CreateQuestionJSONSchema).optional(),
});
export type CreateSurveyVersionJSONDto = z.infer<
  typeof CreateSurveyVersionJSONSchema
>;

export const assignmentConfigurationSchema = z
  .object({
    clientIds: z.array(z.number()).optional().nullable(),
    segmentIds: z.array(z.number()).optional().nullable(),
    stageIds: z.array(z.number()).optional().nullable(),
    availableWindow: z
      .object({
        start: z.number().optional().nullable(),
        end: z.number().optional().nullable(),
      })
      .optional(),
  })
  .strict();

export const LearnMoreContentSurveySchema = z.object({
  title: z.string().optional(),
  sections: z.array(SectionContentSchema).optional(),
  selectedLearnMoreRecordId: z.number().default(-1),
});

export const CreateSurveyContentSchema = z.object({
  id: z.number().optional().default(-1),
  name: z.string().nonempty('Required'),
  key: z.string().nonempty('Required'),
  displayName: z.string().optional(),
  summary: z.string().nonempty('Required'),
  learnMoreContent: LearnMoreContentSurveySchema.optional(),
  assignmentConfiguration: assignmentConfigurationSchema.optional().nullable(),
  isActionSurvey: z.boolean().default(false),
  initialCheckpointDueAtDays: z.number().optional(),
  surveyVersion: CreateSurveyVersionJSONSchema.optional(),
});
export type CreateSurveyContentDto = z.infer<typeof CreateSurveyContentSchema>;

export const CreateSurveyJSONSchema = z.object({
  key: z.string().nonempty('Required'),
  surveyContent: CreateSurveyContentSchema,
  selectedSurveyCreatorRecordId: z.number().optional().default(-1),
  selectedLearnMoreRecordId: z.number().optional().default(-1),
});
export type CreateSurveyJSONDto = z.infer<typeof CreateSurveyJSONSchema>;

export const DEFAULT_OPTION: CreateOptionJSONDto = {
  sortOrder: 1,
  text: '',
  isAlreadyCompletedOption: false,
  isCompletedWithReemaOption: false,
  isRefusalOption: false,
  isVerificationOption: false,
};

export const DEFAULT_QUESTION: CreateQuestionJSONDto = {
  sortOrder: 1,
  key: 'question-1',
  title: '',
  text: '',
  type: 'text' as InputType,
  isRequired: true,
  validationConfiguration: {
    isRequired: true,
  },
  options: [],
};

export const DEFAULT_SURVEY_VERSION_CONTENT: CreateSurveyVersionJSONDto = {
  defaultFinalCheckpointTitle: '',
  defaultFinalCheckpointMessage: '',
  defaultFinalCheckpointSummary: '',
  initialQuestionKey: '',
  questions: [],
};

export const DEFAULT_SURVEY_CONTENT: CreateSurveyJSONDto = {
  key: '',
  surveyContent: {
    name: '',
    key: '',
    displayName: '',
    summary: '',
    learnMoreContent: DEFAULT_LEARN_MORE_CONTENT,
    assignmentConfiguration: null,
    isActionSurvey: false,
    surveyVersion: DEFAULT_SURVEY_VERSION_CONTENT,
  },
};

export type UpdateSurveyJSONDto = Partial<CreateSurveyJSONDto>;

export type UpdateAssignmentConfiguration = z.infer<
  typeof assignmentConfigurationSchema
>;

export const SegmentOverride = z
  .object({
    segmentId: z.number(),
    offsetValue: z.union([z.number(), z.enum(['avgProgramDays'])]).nullable(),
    maxMonth: z.number().optional().nullable(),
  })
  .strict();

export const assignmentDueConfigurationSchema = z
  .object({
    offsetValue: z.union([z.number(), z.enum(['avgProgramDays'])]).nullable(),
    maxMonth: z.number().optional().nullable(),
    segmentOverrides: z.array(SegmentOverride).optional(),
    fixedDate: z.string().optional().nullable(),
  })
  .strict();
export type UpdateAssignmentDueConfiguration = z.infer<
  typeof assignmentDueConfigurationSchema
>;

export const UpdateAssignmentConfigSchema = z
  .object({
    assignmentConfiguration: assignmentConfigurationSchema,
    dueAtConfig: assignmentDueConfigurationSchema,
  })
  .superRefine((data, ctx) => {
    const availableWindow = data.assignmentConfiguration.availableWindow;
    if (!availableWindow.start && !!availableWindow.end) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_type,
        expected: 'number',
        received: typeof data.assignmentConfiguration.availableWindow,
        path: ['assignmentConfiguration.availableWindow.start'],
        message: 'First month field is required if setting Last month field.',
      });
    } else if (!!availableWindow.start && !availableWindow.end) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_type,
        expected: 'number',
        received: typeof data.assignmentConfiguration.availableWindow,
        path: ['assignmentConfiguration.availableWindow.end'],
        message: 'Last month field is required if setting First month field.',
      });
    }
    if (availableWindow.start && availableWindow.end) {
      if (availableWindow.start > availableWindow.end) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          expected: 'number',
          received: typeof data.assignmentConfiguration.availableWindow,
          path: ['assignmentConfiguration.availableWindow.start'],
          message: 'Start month cannot be later than end month.',
        });
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_type,
          expected: 'number',
          received: typeof data.assignmentConfiguration.availableWindow,
          path: ['assignmentConfiguration.availableWindow.end'],
          message: 'End month cannot be before start month.',
        });
      }
    }
  });

export type UpdateAssignmentConfigRequest = z.infer<
  typeof UpdateAssignmentConfigSchema
>;

export interface UpdateAssignmentConfigDto {
  assignmentConfiguration: UpdateAssignmentConfiguration; // TODO switch these to DTO
  dueAtConfig: UpdateAssignmentDueConfiguration;
}

export const LearnMoreFormSchema = z.object({
  title: z.string().nonempty('Required'),
  selectedLearnMoreRecordId: z.number().default(-1),
});

export type LearnMoreFormData = z.infer<typeof LearnMoreFormSchema>;
